import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import Loading from "../components/Loading";
import TicketStub from "../components/TicketStub";
import type {
  CreateOrderInput,
  EventDetail,
  Order,
  TicketGroup,
} from "../types/api";
import styles from "./CheckoutPage.module.css";

const SERVICE_FEE_RATE = 0; // We advertise all-in pricing.

type Selection = {
  detail: EventDetail;
  group: TicketGroup;
  quantity: number;
};

export default function CheckoutPage() {
  const [params] = useSearchParams();
  const eventId = params.get("event");
  const groupId = params.get("group");
  const quantityParam = Number(params.get("qty") ?? "1");

  const [selection, setSelection] = useState<Selection | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    cardNumber: "",
    cardExpiry: "",
    cardCvv: "",
    address1: "",
    city: "",
    state: "",
    postalCode: "",
    country: "US",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<Order | null>(null);

  useEffect(() => {
    if (!eventId || !groupId) {
      setLoadError("missing");
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([api.getEvent(eventId), api.getEventTickets(eventId)])
      .then(([detail, tickets]) => {
        const group = tickets.ticketGroups.find((g) => g.id === groupId);
        if (!group) {
          setLoadError("This ticket is no longer available.");
          return;
        }
        const quantity =
          group.splits.includes(quantityParam)
            ? quantityParam
            : (group.splits[0] ?? 1);
        setSelection({ detail, group, quantity });
      })
      .catch((e: Error) => setLoadError(e.message))
      .finally(() => setLoading(false));
  }, [eventId, groupId, quantityParam]);

  // ------------ Confirmation screen ------------
  if (confirmation && selection) {
    return (
      <ConfirmationView
        confirmation={confirmation}
        selection={selection}
        email={form.email}
      />
    );
  }

  // ------------ Loading ------------
  if (loading) {
    return (
      <div className={`container ${styles.page}`}>
        <Loading />
      </div>
    );
  }

  // ------------ Empty / error state ------------
  if (loadError || !selection) {
    return (
      <div className={`container ${styles.page}`}>
        <div className={styles.empty}>
          <div className={styles.emptyIcon} aria-hidden>
            ◯
          </div>
          <h2 className={styles.emptyTitle}>No ticket selected</h2>
          <p className={styles.emptyDesc}>
            {loadError === "missing"
              ? "Pick a seat from an event to start checkout."
              : loadError ?? "Something went wrong."}
          </p>
          <Link to="/" className="btn btn-primary btn-lg">
            Browse events
          </Link>
        </div>
      </div>
    );
  }

  // ------------ Active checkout ------------
  const { detail, group, quantity } = selection;
  const event = detail.event;
  const venue = detail.venue;
  const subtotal = group.pricePerTicket * quantity;
  const fees = subtotal * SERVICE_FEE_RATE;
  const total = subtotal + fees;

  function update<K extends keyof typeof form>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function formatCardNumber(v: string) {
    return v
      .replace(/\D/g, "")
      .slice(0, 19)
      .replace(/(\d{4})(?=\d)/g, "$1 ");
  }

  function formatExpiry(v: string) {
    const digits = v.replace(/\D/g, "").slice(0, 4);
    if (digits.length < 3) return digits;
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const payload: CreateOrderInput = {
      ticketGroupId: group.id,
      quantity,
      customer: {
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
      },
      billing: {
        address1: form.address1,
        city: form.city,
        state: form.state,
        postalCode: form.postalCode,
        country: form.country,
      },
    };
    try {
      const order = await api.createOrder(payload);
      setConfirmation(order);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={`container ${styles.page}`}>
      <div className={styles.topBar}>
        <Link to={`/events/${event.id}`} className={styles.backLink}>
          ← Back to event
        </Link>
      </div>

      <div className="banner-warning">
        Mock checkout. No payment will be charged and no real ticket will be
        reserved.
      </div>

      <h1 className={styles.title}>Checkout</h1>

      <div className={styles.layout}>
        <form className={styles.form} onSubmit={submit}>
          {/* Step 1 — Contact */}
          <Step number={1} title="Contact info" subtitle="Where to send your tickets">
            <div className={styles.grid}>
              <Field label="Email" full>
                <input
                  type="email"
                  required
                  className="input"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                />
              </Field>
              <Field label="First name">
                <input
                  required
                  className="input"
                  value={form.firstName}
                  onChange={(e) => update("firstName", e.target.value)}
                />
              </Field>
              <Field label="Last name">
                <input
                  required
                  className="input"
                  value={form.lastName}
                  onChange={(e) => update("lastName", e.target.value)}
                />
              </Field>
            </div>
          </Step>

          {/* Step 2 — Payment */}
          <Step number={2} title="Payment" subtitle="Pay quickly with a wallet, or use a card">
            <div className={styles.wallets}>
              <button type="button" className={styles.walletApple} disabled>
                <span className={styles.walletLogo}></span>Pay
              </button>
              <button type="button" className={styles.walletGoogle} disabled>
                <span className={styles.walletGoogleG}>G</span>Pay
              </button>
            </div>
            <div className={styles.divider}>
              <span>or pay with card</span>
            </div>
            <div className={styles.grid}>
              <Field label="Card number" full>
                <input
                  className="input"
                  inputMode="numeric"
                  placeholder="1234 5678 9012 3456"
                  value={form.cardNumber}
                  onChange={(e) =>
                    update("cardNumber", formatCardNumber(e.target.value))
                  }
                />
              </Field>
              <Field label="Expiry">
                <input
                  className="input"
                  inputMode="numeric"
                  placeholder="MM/YY"
                  value={form.cardExpiry}
                  onChange={(e) =>
                    update("cardExpiry", formatExpiry(e.target.value))
                  }
                />
              </Field>
              <Field label="CVV">
                <input
                  className="input"
                  inputMode="numeric"
                  placeholder="123"
                  maxLength={4}
                  value={form.cardCvv}
                  onChange={(e) =>
                    update("cardCvv", e.target.value.replace(/\D/g, ""))
                  }
                />
              </Field>
            </div>
          </Step>

          {/* Step 3 — Billing */}
          <Step number={3} title="Billing address" subtitle="Must match your card on file">
            <div className={styles.grid}>
              <Field label="Address" full>
                <input
                  required
                  className="input"
                  value={form.address1}
                  onChange={(e) => update("address1", e.target.value)}
                />
              </Field>
              <Field label="City">
                <input
                  required
                  className="input"
                  value={form.city}
                  onChange={(e) => update("city", e.target.value)}
                />
              </Field>
              <Field label="State">
                <input
                  required
                  className="input"
                  value={form.state}
                  onChange={(e) => update("state", e.target.value)}
                />
              </Field>
              <Field label="Postal code">
                <input
                  required
                  className="input"
                  value={form.postalCode}
                  onChange={(e) => update("postalCode", e.target.value)}
                />
              </Field>
              <Field label="Country">
                <input
                  required
                  className="input"
                  value={form.country}
                  onChange={(e) => update("country", e.target.value)}
                />
              </Field>
            </div>
          </Step>

          {error && <div className={styles.error}>{error}</div>}

          <button
            type="submit"
            className={`btn btn-primary btn-lg ${styles.placeOrder}`}
            disabled={submitting}
          >
            {submitting ? "Placing order..." : `Place order · $${total.toFixed(2)}`}
          </button>

          <div className={styles.trust}>
            <Trust icon="✓" text="100% guaranteed" />
            <Trust icon="⚡" text="Instant delivery" />
            <Trust icon="🔒" text="Secure checkout" />
          </div>
        </form>

        <aside className={styles.summary}>
          <div className={styles.stubWrap}>
            <TicketStub
              eventId={event.id}
              eventName={event.name}
              eventDatetime={event.datetime}
              venueName={venue.name}
              section={group.section}
              row={group.row}
              quantity={quantity}
              categoryId={event.categoryId}
            />
          </div>

          <div className={styles.priceBlock}>
            <PriceRow
              label={`${quantity} × $${group.pricePerTicket.toFixed(2)}`}
              value={`$${subtotal.toFixed(2)}`}
            />
            <PriceRow
              label="Service fees"
              value={<span className={styles.included}>Included</span>}
            />
            <PriceRow
              label="Delivery"
              value={<span className={styles.included}>Free · Instant</span>}
            />
            <div className={styles.totalRow}>
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---------------- Inline pieces ----------------

function Step({
  number,
  title,
  subtitle,
  children,
}: {
  number: number;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={styles.step}>
      <header className={styles.stepHeader}>
        <span className={styles.stepNumber}>{number}</span>
        <div>
          <h2 className={styles.stepTitle}>{title}</h2>
          {subtitle && <p className={styles.stepSubtitle}>{subtitle}</p>}
        </div>
      </header>
      <div className={styles.stepBody}>{children}</div>
    </section>
  );
}

function Field({
  label,
  full,
  children,
}: {
  label: string;
  full?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className={`${styles.field} ${full ? styles.fieldFull : ""}`}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

function PriceRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className={styles.priceRow}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function Trust({ icon, text }: { icon: string; text: string }) {
  return (
    <div className={styles.trustItem}>
      <span className={styles.trustIcon} aria-hidden>
        {icon}
      </span>
      <span>{text}</span>
    </div>
  );
}

// ---------------- Confirmation view ----------------

function ConfirmationView({
  confirmation,
  selection,
  email,
}: {
  confirmation: Order;
  selection: Selection;
  email: string;
}) {
  const { detail, group, quantity } = selection;
  return (
    <div className={`container ${styles.confirmPage}`}>
      <div className={styles.confirmHead}>
        <div className={styles.successBadge}>
          <span className={styles.successDot} />
          Order confirmed
        </div>
        <h1 className={styles.confirmTitle}>You're going!</h1>
        <p className={styles.confirmDesc}>
          Confirmation #<strong>{confirmation.orderId}</strong>
          {email ? (
            <>
              {" "}· Tickets sent to <strong>{email}</strong>
            </>
          ) : null}
        </p>
      </div>

      <div className={styles.confirmStubWrap}>
        <TicketStub
          size="lg"
          eventId={detail.event.id}
          eventName={detail.event.name}
          eventDatetime={detail.event.datetime}
          venueName={detail.venue.name}
          section={group.section}
          row={group.row}
          quantity={quantity}
          categoryId={detail.event.categoryId}
          orderId={confirmation.orderId}
        />
      </div>

      <div className={styles.confirmTotals}>
        <div>
          <div className={styles.confirmTotalLabel}>Tickets</div>
          <div className={styles.confirmTotalValue}>{confirmation.quantity}</div>
        </div>
        <div>
          <div className={styles.confirmTotalLabel}>Total paid</div>
          <div className={styles.confirmTotalValue}>
            ${confirmation.total.toFixed(2)}
          </div>
        </div>
      </div>

      <h3 className={styles.whatsNextTitle}>What's next</h3>
      <ul className={styles.whatsNext}>
        <li>
          <span className={styles.checkIcon}>✓</span>
          Confirmation email sent to your inbox
        </li>
        <li>
          <span className={styles.checkIcon}>✓</span>
          Mobile tickets ready in your account
        </li>
        <li>
          <span className={styles.checkIcon}>✓</span>
          Add the event to your calendar
        </li>
      </ul>

      <div className="banner-warning">
        No payment was processed and no real ticket was reserved.
      </div>

      <Link to="/" className="btn btn-primary btn-lg">
        Browse more events
      </Link>
    </div>
  );
}
